import { tagPlus, wandAlsZeitpunkt } from "./betriebstag";
import type { Einsatzfrage } from "./einteilen";

/* ══════════════════════════════════════════════════════════════════
   Das Fenster „Ortsaufnahme anlegen" — die Regeln, rein und ohne React
   (08.10.2026, Paket ortsaufnahme; Test `test/ortsaufnahme-anlegen.test.ts`).

   Luca: *„Wenn ich das selber mache, mach ein vernünftiges Pop-up, sodass
   man ein Aufmaß anlegen und eine Ortsaufnahme aufnehmen kann. Ich muss
   Ortsaufnahmen auch verteilen können."*

   Was hier steht: aus dem Entwurf des Fensters wird der Rumpf für
   `POST /aufnahmen`, und was noch fehlt, wird als Satz gesagt (ein Knopf
   ist nie stumm gesperrt, docs/64 Regel 9).

   | Wann | mit Person | ohne Person |
   |---|---|---|
   | Tag + Uhrzeit | **Termin** im Band der Person (0180) | **Wunsch** `genau` — unter „Offen" an diesem Tag |
   | Woche | Wunsch `kw` — Marke in der Zeile | Wunsch `kw` — unter „Offen", „KW 42" |
   | noch offen | nur die Person | unter „Offen", „Ohne Termin" |
   ══════════════════════════════════════════════════════════════════ */

export type Wannart = "offen" | "tag" | "kw";

/** Was im Fenster steht. */
export type Ortsaufnahmeentwurf = {
  /** Die Kundennummer — leer, solange keiner gewählt ist. Die Kennung löst das Anlegen auf. */
  kundeNr: string;
  /** Die Bezeichnung des Einsatzorts, wie in der Akte — leer heißt: noch offen (Neukunde). */
  ort: string;
  leistungId: string;
  /** Eine Bestellung aus dem Angebot heraus: Kunde, Ort, Leistung stehen an der Position. */
  positionId: string;
  wann: Wannart;
  /** Bei „Tag": der Tag und die Uhrzeiten `HH:MM`. */
  tag: string | null;
  von: string;
  bis: string;
  /** Bei „Woche": der Montag. */
  woche: string | null;
  /** Wer — leer heißt: unter „Offen" in Einteilen. */
  person: string;
  hinweis: string;
};

/** Was noch fehlt — der i18n-Schlüssel des Satzes, oder `null`, wenn angelegt werden kann. */
export function fehltNoch(e: Ortsaufnahmeentwurf): string | null {
  if (!e.positionId) {
    if (!e.kundeNr) return "ortsaufnahmeAnlegen.fehltKunde";
    if (!e.leistungId) return "ortsaufnahmeAnlegen.fehltLeistung";
  }
  if (e.wann === "tag") {
    if (!e.tag) return "ortsaufnahmeAnlegen.fehltTag";
    if (!e.von || !e.bis || e.bis <= e.von) return "ortsaufnahmeAnlegen.fehltUhrzeit";
  }
  if (e.wann === "kw" && !e.woche) return "ortsaufnahmeAnlegen.fehltWoche";
  return null;
}

/** Die Woche ab Montag als Zeitpunkte: Montag 00:00 bis Montag 00:00 im Betrieb (0285). */
export function wochenfenster(montag: string): { von: string; bis: string } {
  return {
    von: wandAlsZeitpunkt(montag, "00:00").toISOString(),
    bis: wandAlsZeitpunkt(tagPlus(montag, 7), "00:00").toISOString(),
  };
}

/** Tag und Uhrzeiten als Zeitpunkte — in der Uhr des Betriebs, wie der Termin sie zeigt. */
export function tagesfenster(tag: string, von: string, bis: string): { von: string; bis: string } {
  return { von: wandAlsZeitpunkt(tag, von).toISOString(), bis: wandAlsZeitpunkt(tag, bis).toISOString() };
}

/**
 * **Der Rumpf für `POST /aufnahmen`.** `person` geht immer mit — auch leer:
 * `person: null` heißt ausdrücklich „unter Offen", ein fehlendes Feld hieße
 * „der, der anlegt" (der Weg des Telefons).
 *
 * `selbst` ist „Anlegen und gleich aufnehmen": Dann bekommt sie, wer anlegt.
 * `ids` sind die Kennungen von Kunde und Ort, aufgelöst aus der Akte (die
 * Maske kennt nur Nummer und Bezeichnung).
 */
export function anlegeRumpf(
  e: Ortsaufnahmeentwurf,
  ids: { kundeId: string; ortId: string },
  selbst: string | null = null,
): Record<string, unknown> {
  const person = selbst ?? (e.person || null);
  const rumpf: Record<string, unknown> = e.positionId
    ? { position_id: e.positionId }
    : { kunde_id: ids.kundeId, leistungsart_id: e.leistungId, ...(ids.ortId ? { ort_id: ids.ortId } : {}) };
  rumpf["person"] = person;
  if (e.wann === "tag" && e.tag) {
    const f = tagesfenster(e.tag, e.von, e.bis);
    if (person) rumpf["termin"] = f;
    else rumpf["wunsch"] = { ...f, schaerfe: "genau" };
  } else if (e.wann === "kw" && e.woche) {
    rumpf["wunsch"] = { ...wochenfenster(e.woche), schaerfe: "kw" };
  }
  if (e.hinweis.trim()) rumpf["notiz"] = e.hinweis.trim();
  return rumpf;
}

/**
 * **Die Frage für den Vorschlag** — dieselbe Form wie bei einem Einsatz ohne
 * Person (`vorschlagFuerEinsatz`, `daten/einteilen.ts`): das Fenster und was
 * die Leistung verlangt. Ohne Tag ein Augenblick: Dann zählt nur die
 * Qualifikation.
 */
export function vorschlagsfrage(e: Ortsaufnahmeentwurf, verlangt: string[], heute: string): Einsatzfrage {
  const leer = wandAlsZeitpunkt(heute, "07:00").toISOString();
  const f = e.wann === "tag" && e.tag && e.von && e.bis && e.bis > e.von ? tagesfenster(e.tag, e.von, e.bis)
    : e.wann === "kw" && e.woche ? wochenfenster(e.woche)
      : { von: leer, bis: leer };
  return { ...f, verlangt, position_id: "neu" };
}

/** Die Tage, über die das Fenster die Lage fragt (`GET /aufnahmen/lage`). */
export function lagetage(e: Ortsaufnahmeentwurf, heute: string): { von: string; bis: string } {
  if (e.wann === "tag" && e.tag) return { von: e.tag, bis: e.tag };
  if (e.wann === "kw" && e.woche) return { von: e.woche, bis: tagPlus(e.woche, 6) };
  return { von: heute, bis: tagPlus(heute, 6) };
}

/* ══════════════════════════════════════════════════════════════════
   Die Liste „Ortsaufnahmen" — Stand und Filter (Paket aufnahmen2,
   08.10.2026; Test `test/aufnahme-zuordnen.test.ts`).

   Luca: *„Jetzt habe ich aber nicht die Möglichkeit, die Ortsaufnahmen
   irgendwo wieder anzugucken."* Die Liste steht wieder in der Seitenleiste
   (Disposition), und sie sagt je Zeile, wo die Aufnahme steht:

   | Stand | heißt | Filter |
   |---|---|---|
   | offen | niemand hat sie, kein Termin — sie liegt unter „Offen" in Einteilen | Offen |
   | geplant | jemand hat sie oder ein Termin steht, gemessen ist noch nicht | Geplant |
   | erfasst | das Aufmaß ist da, ein Angebot fehlt noch (Befund 41) | Erfasst |
   | erledigt | gemessen und im Angebot, oder ohne Angebot abgeschlossen | Erfasst |
   | verworfen | die Fahrt entfällt | nur unter Alle |
   ══════════════════════════════════════════════════════════════════ */

export type Aufnahmestand = "offen" | "geplant" | "erfasst" | "erledigt" | "verworfen";
export type Aufnahmefilter = "offen" | "geplant" | "erfasst" | "alle";

export function aufnahmeStand(a: {
  status: string; gemessen: boolean; werId: string | null; termin: unknown;
}): Aufnahmestand {
  if (a.status === "verworfen") return "verworfen";
  if (a.status === "erledigt") return "erledigt";
  if (a.gemessen) return "erfasst";
  return a.werId || a.termin ? "geplant" : "offen";
}

export function imAufnahmefilter(stand: Aufnahmestand, filter: Aufnahmefilter): boolean {
  if (filter === "alle") return true;
  if (filter === "erfasst") return stand === "erfasst" || stand === "erledigt";
  return stand === filter;
}

/**
 * Lässt sich diese Aufnahme einem Angebot zuordnen? Sie gehört noch keinem
 * Vorgang, ist nicht verworfen und weiß, wofür gemessen wird — dieselben
 * Riegel wie `POST /aufnahmen/:id/zuordnen`.
 */
export function zuordenbar(a: { status: string; vorgang: string | null; leistungsartId: string | null }): boolean {
  return a.status !== "verworfen" && !a.vorgang && Boolean(a.leistungsartId);
}

/**
 * „Zuordnung lösen" gibt es nur, solange das Angebot ein Entwurf ist — danach
 * stehen seine Mengen auf dem Papier beim Kunden.
 */
export function loesbar(a: { status: string; vorgang: string | null; vorgangStatus?: string | null | undefined }): boolean {
  return Boolean(a.vorgang) && a.status !== "verworfen" && a.vorgangStatus === "entwurf";
}

/* ══════════════════════════════════════════════════════════════════
   Der Verweis auf den Vorgang (Paket aufnahmeakte, 08.10.2026).

   Luca: *„Fix den Verweis auf einen Vorgang auf ‚V'."* Überall, wo eine
   Ortsaufnahme auf ihren Vorgang zeigt — Akte, Listenblatt, Liste —, steht
   dieselbe Kennung mit derselben Art und führt in dieselbe Akte:

   | Zustand des Vorgangs | Art | Kennung | Weg |
   |---|---|---|---|
   | entwurf · angebot · abgelehnt | Angebot | die Angebotsnummer AG-…, sonst die Nummer | `/angebote/…` |
   | jeder andere (angenommen, läuft, abgerechnet, storniert) | Auftrag | die Vorgangsnummer V-… | `/auftraege/…` |

   Vorher stand am Telefon und in der Akte „Ins Angebot V-2026-0002
   wechseln" — ein Auftrag unter dem Namen des Angebots, und der Verweis
   ging über `/angebote/` (dort leitete die Akte erst weiter). Dieselbe
   Phasengrenze wie `vorgangsweg` in `belege.ts`; hier ohne den Import,
   damit die Regel ohne React prüfbar bleibt.
   ══════════════════════════════════════════════════════════════════ */

const ANGEBOTSPHASE = new Set(["entwurf", "angebot", "abgelehnt"]);

export type Vorgangsverweis = {
  art: "angebot" | "auftrag";
  /** Die Kennung, unter der der Vorgang geführt wird — Mono in der Oberfläche. */
  kennung: string;
  weg: "/angebote/$nr" | "/auftraege/$nr";
};

export function vorgangsverweis(a: {
  vorgang?: string | null | undefined;
  vorgangStatus?: string | null | undefined;
  angebotNummer?: string | null | undefined;
}): Vorgangsverweis | null {
  const nr = (a.vorgang ?? "").trim();
  if (!nr) return null;
  const status = (a.vorgangStatus ?? "").trim();
  /* Ohne Zustand (älterer Stand der Antwort) entscheidet die Form der Nummer:
     Ein Entwurf trägt seit 0200 die Angebotsnummer als Nummer. */
  const angebot = status ? ANGEBOTSPHASE.has(status) : /^AG/i.test(nr);
  if (angebot) {
    return { art: "angebot", kennung: (a.angebotNummer ?? "").trim() || nr, weg: "/angebote/$nr" };
  }
  return { art: "auftrag", kennung: nr, weg: "/auftraege/$nr" };
}

/**
 * **Wohin der Pfeil der Akte zurückgeht** (Paket aufnahmeakte): dorthin,
 * woher man kam. Aus einem Angebot oder Auftrag zurück in genau diese Akte,
 * aus Einteilen nach Einteilen, von „Heute" nach „Heute", sonst in die Liste.
 */
export type Aufnahmerueckweg =
  | { art: "liste"; zu: "/heute" | "/aufnahmen" | "/einteilen"; wort: string }
  | { art: "akte"; pfad: string; vorgang: "angebot" | "auftrag" };

export function aufnahmeRueckweg(woher: string | null): Aufnahmerueckweg {
  const akte = /^\/(angebote|auftraege)\/([^/]+)$/.exec(woher ?? "");
  if (akte && akte[2] !== "neu") {
    return { art: "akte", pfad: woher as string, vorgang: akte[1] === "angebote" ? "angebot" : "auftrag" };
  }
  if (woher === "/einteilen") return { art: "liste", zu: "/einteilen", wort: "einteilen.titel" };
  /* `/start` ist die Altadresse von „Heute" (bis 21.09.2026 ein eigener Schirm). */
  if (woher === "/heute" || woher === "/start") return { art: "liste", zu: "/heute", wort: "handy.titel" };
  return { art: "liste", zu: "/aufnahmen", wort: "aufnahmen.titel" };
}
